import {
  toLineBreak,
  toLinkContent,
  toParagraphContent,
} from '@principle-theorem/editor';
import {
  MarketingCampaign,
  MarketingCampaignStep,
  Practice,
  RecurrencePattern,
} from '@principle-theorem/principle-core';
import {
  IPractice,
  MarketingCampaignStepConversionType,
  MarketingCampaignType,
  TemplateScope,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  FactTables,
  healthFundExpiringCalendarYearCampaignReportTemplate,
} from '@principle-theorem/reporting';
import {
  asReffable,
  CustomRecurrenceFrequency,
  DocumentReference,
  Frequency,
  Region,
  toTimestamp,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { IMarketingCampaignTemplate } from './marketing-campaign-template';
import { MarketingCampaignTemplateFactory } from './marketing-campaign-template-factory';
import { CampaignTemplateHelpers } from './campaign-template-helpers';

export function getNextCampaignStartDate(date: {
  month: number | string;
  day: number;
}): Moment {
  const now = moment();
  let targetDate = moment().month(date.month).date(date.day).startOf('day');
  if (targetDate.isBefore(now)) {
    targetDate = targetDate.add(1, 'year');
  }
  return targetDate;
}

export function getBaseHealthFundCampaignTemplate(
  practiceRef: DocumentReference<IPractice>,
  nextRunStartDate: Moment,
  expiryDate: Moment
): IMarketingCampaignTemplate {
  const expiryDateLong = expiryDate.format('Do MMMM');
  const expiryDateShort = expiryDate.format('D MMM');

  return {
    name: 'Health Fund Expiring Benefits Calendar Year Campaign',
    description: `Remind your patients to use their expiring health fund benefits before the end of the calendar year with timely SMS and email reminders.`,
    regions: [Region.Australia],
    campaign: MarketingCampaign.init({
      brandRef: Practice.brandRef(asReffable(practiceRef)),
      practiceRef,
      name: 'Health Fund Expiring Benefits Calendar Year Campaign',
      type: MarketingCampaignType.GeneralNotice,
      scope: TemplateScope.Patient,
      nextRunAt: toTimestamp(nextRunStartDate),
      entryResetRules: {
        multipleEntry: true,
        reentryScope: TemplateScope.Patient,
        maxEntryCount: undefined,
        frequencyCount: 1,
        frequencyType: CustomRecurrenceFrequency.Yearly,
      },
      recurrencePattern: RecurrencePattern.init({
        customFrequencyType: CustomRecurrenceFrequency.Daily,
        frequencyType: Frequency.Custom,
      }),
      recurrenceRunTime: '10:00',
      conversionType: MarketingCampaignStepConversionType.AnyAppointmentBooked,
    }),
    report: healthFundExpiringCalendarYearCampaignReportTemplate(
      FactTables.patientEvent.latestEvent
    ),
    steps: [
      MarketingCampaignStep.init({
        name: 'SMS Reset Reminder 1',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                `Hi {{ patientFirstName }}, your health fund benefits reset on ${expiryDateShort} - use them before they disappear!`,
                'Book in at {{ practiceName }} today 👉 {{ appointmentBookingLink }} or call {{ practicePhoneNumber }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 1,
            unit: TimingUnit.Minutes,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Email Reset Reminder 1 (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'Use your {{ nowYear }} benefits before they reset',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                `Did you know your health fund benefits reset on ${expiryDateLong}? That means any unused dental benefits will disappear - and we’d hate for you to miss out.`,
                toLineBreak(),
                'Now’s the perfect time to book in for any check-ups or treatments you’ve been putting off, while you can still make the most of your current benefits.',
                toLineBreak(),
                '📍 {{ practiceName }}',
                [
                  '👉 Book online: ',
                  toLinkContent('{{ appointmentBookingLink }}'),
                ],
                [
                  '📞 Or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
                toLineBreak(),
                'Appointments are already filling fast as patients look to use their benefits before the reset, so we encourage you to secure a time that works best for you.',
                toLineBreak(),
                'We look forward to seeing you soon!',
                toLineBreak(),
                'Warm regards,',
                '{{ practiceName }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 1,
            unit: TimingUnit.Minutes,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
          ])
        ),
      }),

      MarketingCampaignStep.init({
        name: 'SMS Reset Reminder 2',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                `Hi {{ patientFirstName }}, your health fund benefits reset on ${expiryDateShort} - use them before they disappear!`,
                'Book in at {{ practiceName }} today 👉 {{ appointmentBookingLink }} or call {{ practicePhoneNumber }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 1,
            unit: TimingUnit.Months,
            time: '13:00',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Email Reset Reminder 2 (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'Use your {{ nowYear }} benefits before they reset',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                `Did you know your health fund benefits reset on ${expiryDateLong}? That means any unused dental benefits will disappear - and we’d hate for you to miss out.`,
                toLineBreak(),
                'Now’s the perfect time to book in for any check-ups or treatments you’ve been putting off, while you can still make the most of your current benefits.',
                toLineBreak(),
                '📍 {{ practiceName }}',
                [
                  '👉 Book online: ',
                  toLinkContent('{{ appointmentBookingLink }}'),
                ],
                [
                  '📞 Or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
                toLineBreak(),
                'Appointments are already filling fast as patients look to use their benefits before the reset, so we encourage you to secure a time that works best for you.',
                toLineBreak(),
                'We look forward to seeing you soon!',
                toLineBreak(),
                'Warm regards,',
                '{{ practiceName }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 1,
            unit: TimingUnit.Minutes,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
          ])
        ),
      }),
    ],
  };
}

export function getHealthFundExpiringCalendarYearCampaignTemplate(
  practiceRef: DocumentReference<IPractice>
): IMarketingCampaignTemplate {
  const nextRunDate = getNextCampaignStartDate({ month: 11, day: 1 });
  const expiryDate = moment(nextRunDate).add(2, 'months');
  return getBaseHealthFundCampaignTemplate(
    practiceRef,
    nextRunDate,
    expiryDate
  );
}
